/**
 * TenantContext — resolves the current hostname against the backend on first
 * load, makes the white-label config available app-wide, and applies the
 * brand color + favicon + page title.
 *
 * Use:   const tenant = useTenant();
 *        if (tenant) {  ...render white-label  }  else {  ...normal site  }
 */
import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";

const TenantContext = createContext({ tenant: null, loading: true });

export const useTenant = () => useContext(TenantContext).tenant;
export const useTenantLoading = () => useContext(TenantContext).loading;

/** Returns the user-facing brand name. Prefers an explicitly-set
 *  `branding.email_from_name` (creator's brand display), falls back to the
 *  tier_name (tier label), and finally to "Propix3ls" for the platform. */
export const useBrandName = () => {
  const t = useContext(TenantContext).tenant;
  if (!t) return "Propix3ls";
  return t.branding?.email_from_name || t.tier_name || "Propix3ls";
};

/** Returns the contact email for this tenant. On a tenant route we never
 *  fall back to a Propix3ls email — returns "" so the caller can hide the
 *  "contact us" line entirely if no creator_email is configured. */
export const useTenantContactEmail = () => {
  const t = useContext(TenantContext).tenant;
  if (!t) return "";
  return t.creator_email || "";
};

export function TenantProvider({ children }) {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Allow `localStorage.setItem("tenant_host_override", "bobtest.propix3ls.com")`
    // for previewing/debugging a tenant build without real DNS in place.
    const override = (typeof window !== "undefined" && window.localStorage?.getItem("tenant_host_override")) || "";
    const host = override || window.location.hostname;
    axios
      .get(`${API}/branding/resolve?host=${encodeURIComponent(host)}`)
      .then((r) => setTenant(r.data?.tier_id ? r.data : null))
      .catch(() => setTenant(null))
      .finally(() => setLoading(false));
  }, []);

  // Apply primary color as a CSS variable + page title + favicon
  useEffect(() => {
    if (!tenant) return;
    const color = tenant.branding?.primary_color || "#D946EF";
    document.documentElement.style.setProperty("--tenant-primary", color);
    if (tenant.tier_name) document.title = tenant.tier_name;
    const fav = tenant.branding?.favicon_url;
    if (fav) {
      let link = document.querySelector("link[rel='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = fav;
    }
  }, [tenant]);

  return (
    <TenantContext.Provider value={{ tenant, loading }}>
      {children}
    </TenantContext.Provider>
  );
}
